/**
 * Hook personnalisé pour gérer les listes World Anvil
 * Gère le chargement des listes dépendantes et le stockage en localStorage
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocalStorage } from './useLocalStorage';
import waproxyClient from '../utils/waproxy-calls';
import { fetchConfigApi } from '../utils/config-calls';
import debug from '@commons-shared/debug';
import { config } from '../config/environment';

/**
 * Hook pour gérer une liste World Anvil avec dépendances
 * @param {string} storageKey - Clé de stockage localStorage
 * @param {string} apiEndpoint - Endpoint API pour récupérer les données
 * @param {string|null} dependencyId - ID de dépendance pour charger la liste
 * @param {Object} options - Options supplémentaires
 * @returns {Object} État et fonctions de la liste
 */
function useWorldAnvilList(storageKey, apiEndpoint, dependencyId = null, options = {}) {
  const [storedValue, setStoredValue] = useLocalStorage(storageKey, null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fonction pour charger les données
  const loadItems = useCallback(async () => {
    if (!dependencyId && options.requiresDependency) {
      setItems([]);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      debug.log(config, `Loading ${storageKey} with dependency:`, dependencyId);
      
      const response = await waproxyClient.waPost(apiEndpoint, dependencyId);
      
      if (response && response.success && response.entities) {
        setItems(response.entities);
        debug.log(config, `Loaded ${response.entities.length} items for ${storageKey}`);
      } else {
        throw new Error(`Invalid response format for ${storageKey}`);
      }
    } catch (err) {
      debug.error(config, `Error loading ${storageKey}:`, err);
      setError(err.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [storageKey, apiEndpoint, dependencyId, options.requiresDependency]);

  // Charger automatiquement au changement de dépendance
  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Fonction pour sélectionner un élément
  const selectItem = useCallback((item) => {
    if (item && item.id && item.title) {
      const selectedData = {
        id: item.id,
        title: item.title
      };
      setStoredValue(selectedData);
      debug.log(config, `Selected ${storageKey}:`, selectedData);
    }
  }, [storageKey, setStoredValue]);

  // Fonction pour effacer la sélection
  const clearSelection = useCallback(() => {
    setStoredValue(null);
    debug.log(config, `Cleared ${storageKey} selection`);
  }, [storageKey, setStoredValue]);

  return {
    items,
    loading,
    error,
    selectedValue: storedValue,
    selectItem,
    clearSelection,
    reload: loadItems
  };
}

/**
 * Hook principal pour gérer toutes les listes World Anvil
 */
export function useWorldAnvilLists() {
  // ID utilisateur depuis localStorage
  const [waUser] = useLocalStorage('wa_user', null);
  
  // Configuration des listes avec leurs dépendances
  const worlds = useWorldAnvilList(
    'wa_world',
    '/user/worlds',
    waUser?.id,
    { requiresDependency: true }
  );

  const manuscripts = useWorldAnvilList(
    'wa_manuscript',
    '/world/manuscripts',
    worlds.selectedValue?.id,
    { requiresDependency: true }
  );

  const versions = useWorldAnvilList(
    'wa_version',
    '/manuscript/manuscript_versions',
    manuscripts.selectedValue?.id,
    { requiresDependency: true }
  );

  // Gestion des bel_tags - combinaison des tags WA avec la config locale
  const [belTags, setBelTags] = useLocalStorage('bel_tags', {});
  const [belTagsLoading, setBelTagsLoading] = useState(false);
  const [belTagsError, setBelTagsError] = useState(null);
  const [uiLanguage] = useLocalStorage('ui_language', 'FR');

  // Utiliser useRef pour éviter la boucle infinie avec setBelTags
  const setBelTagsRef = useRef(setBelTags);
  setBelTagsRef.current = setBelTags;

  // Fonction pour charger et construire les bel_tags
  const loadBelTags = useCallback(async () => {
    if (!manuscripts.selectedValue?.id) {
      setBelTagsRef.current({});
      setBelTagsError(null);
      return;
    }

    try {
      setBelTagsLoading(true);
      setBelTagsError(null);
      
      debug.log(config, 'Loading bel_tags for manuscript:', manuscripts.selectedValue.id);
      
      // 1. Récupérer les manuscript-tags de la config
      const configTagsResponse = await fetchConfigApi('/api/config/read/manuscript-tags');
      if (!configTagsResponse.success) {
        throw new Error('Failed to load config manuscript tags');
      }
      
      // 2. Récupérer les tags World Anvil pour le manuscrit
      const waTagsResponse = await waproxyClient.waPost('/manuscript/manuscript_tags', manuscripts.selectedValue.id);
      if (!waTagsResponse.success || !waTagsResponse.entities) {
        throw new Error('Failed to load World Anvil manuscript tags');
      }
      
      // 3. Construire la structure bel_tags comme objet indexé
      const configTags = configTagsResponse.manuscriptTags || [];
      const waTags = waTagsResponse.entities || [];
      
      const newBelTags = {};

      // En parcourant ConfigTags pour les tags isDefault
      for (const configTag of configTags) {
        if (configTag.tag_key === 'isDefault' && configTag.ui_language === uiLanguage) {
          newBelTags['00000000-0000-0000-0000-000000000001'] = {
            // Données de World Anvil (vides pour isDefault)
            wa_title: "",
            wa_id: "00000000-0000-0000-0000-000000000001",
            // Données de la config locale
            config_tag_key: configTag.tag_key,
            config_tab_name: configTag.tab_name,
            config_tag_type: configTag.tag_type,
            config_is_meta: configTag.is_meta,
            config_meta_order: configTag.meta_order,
            // Informations combinées
            has_config: true,
            display_label: configTag.label
          };
          break; // Arrêter au premier qui convient
        }
      }
      
      waTags.forEach(waTag => {
        // Trouver le tag de config correspondant par label
        const matchingConfigTag = configTags.find(configTag => 
          configTag.label === waTag.title && 
          configTag.ui_language === uiLanguage
        );
        
        // Ne garder que ceux avec config
        if (matchingConfigTag) {
          newBelTags[waTag.id] = {
            // Données de World Anvil
            wa_title: waTag.title,
            wa_id: waTag.id,
            // Données de la config locale
            config_tag_key: matchingConfigTag.tag_key,
            config_tab_name: matchingConfigTag.tab_name,
            config_tag_type: matchingConfigTag.tag_type,
            config_is_meta: matchingConfigTag.is_meta,
            config_meta_order: matchingConfigTag.meta_order,
            // Informations combinées
            has_config: true,
            display_label: matchingConfigTag.label
          };
        }
      });
      
      setBelTagsRef.current(newBelTags);
      debug.log(config, `Loaded ${Object.keys(newBelTags).length} bel_tags`);
      
    } catch (err) {
      debug.error(config, 'Error loading bel_tags:', err);
      setBelTagsError(err.message);
      setBelTagsRef.current({});
    } finally {
      setBelTagsLoading(false);
    }
  }, [manuscripts.selectedValue?.id, uiLanguage]);

  // Charger les bel_tags quand le manuscrit ou la langue UI change
  useEffect(() => {
    loadBelTags();
  }, [loadBelTags]);

  // Pour les labels, on charge une seule fois et on stocke différemment chaque type
  const [labelsDraft, setLabelsDraft] = useLocalStorage('wa_label_generated', null);
  const [labelsCanon, setLabelsCanon] = useLocalStorage('wa_label_canon', null);
  const [labelsCopied, setLabelsCopied] = useLocalStorage('wa_label_copied', null);
  const [waManuscriptLabels, setWaManuscriptLabels] = useLocalStorage('wa_manuscript_labels', {});
  
  const [labelsItems, setLabelsItems] = useState([]);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [labelsError, setLabelsError] = useState(null);

  // Utiliser useRef pour éviter la re-création de setWaManuscriptLabels
  const setWaManuscriptLabelsRef = useRef(setWaManuscriptLabels);
  setWaManuscriptLabelsRef.current = setWaManuscriptLabels;

  // Fonction utilitaire pour récupérer les détails d'un label avec couleur
  const fetchLabelDetails = useCallback(async (labelId) => {
    try {
      debug.log(config, 'Fetching label details for:', labelId);
      
      // Utiliser waGet avec granularity=2
      const response = await waproxyClient.waGet('/manuscript_label', labelId, 2);
      
      if (response && response.success) {
        return response;
      } else {
        throw new Error('Invalid response format for label details');
      }
    } catch (err) {
      debug.error(config, 'Error fetching label details:', err);
      throw err;
    }
  }, []);

  // Fonction pour charger les labels
  const loadLabels = useCallback(async () => {
    if (!manuscripts.selectedValue?.id) {
      setLabelsItems([]);
      setLabelsError(null);
      setWaManuscriptLabelsRef.current({});
      return;
    }

    try {
      setLabelsLoading(true);
      setLabelsError(null);
      
      debug.log(config, 'Loading manuscript labels for:', manuscripts.selectedValue.id);
      
      const response = await waproxyClient.waPost('/manuscript/manuscript_labels', manuscripts.selectedValue.id);
      
      if (response && response.success && response.entities) {
        setLabelsItems(response.entities);
        debug.log(config, `Loaded ${response.entities.length} labels`);
        
        // Créer la structure wa_manuscript_labels avec les détails de chaque label
        const manuscriptLabelsData = {};
        
        // Récupérer les détails de couleur pour chaque label
        for (const label of response.entities) {
          try {
            const labelDetails = await fetchLabelDetails(label.id);
            manuscriptLabelsData[label.id] = {
              title: label.title,
              color: labelDetails.color || null
            };
          } catch (err) {
            debug.error(config, `Error fetching details for label ${label.id}:`, err);
            // En cas d'erreur, stocker sans couleur
            manuscriptLabelsData[label.id] = {
              title: label.title,
              color: null
            };
          }
        }
        
        setWaManuscriptLabelsRef.current(manuscriptLabelsData);
        debug.log(config, 'Created wa_manuscript_labels structure:', manuscriptLabelsData);
        
      } else {
        throw new Error('Invalid response format for labels');
      }
    } catch (err) {
      debug.error(config, 'Error loading labels:', err);
      setLabelsError(err.message);
      setLabelsItems([]);
      setWaManuscriptLabelsRef.current({});
    } finally {
      setLabelsLoading(false);
    }
  }, [manuscripts.selectedValue?.id]);

  // Charger les labels quand le manuscrit change
  useEffect(() => {
    loadLabels();
  }, [loadLabels]);

  // Fonctions pour sélectionner les différents types de labels
  const selectLabelDraft = useCallback(async (label) => {
    if (label && label.id && label.title) {
      try {
        // Récupérer les détails avec couleur
        const labelDetails = await fetchLabelDetails(label.id);
        
        const selectedData = { 
          id: label.id, 
          title: label.title,
          color: labelDetails.color || null
        };
        setLabelsDraft(selectedData);
        debug.log(config, 'Selected draft label with color:', selectedData);
      } catch (err) {
        // En cas d'erreur, stocker sans couleur
        const selectedData = { id: label.id, title: label.title };
        setLabelsDraft(selectedData);
        debug.log(config, 'Selected draft label without color (error):', selectedData);
      }
    }
  }, []); // Aucune dépendance car fetchLabelDetails et setLabelsDraft sont stables

  const selectLabelCanon = useCallback(async (label) => {
    if (label && label.id && label.title) {
      try {
        // Récupérer les détails avec couleur
        const labelDetails = await fetchLabelDetails(label.id);
        
        const selectedData = { 
          id: label.id, 
          title: label.title,
          color: labelDetails.color || null
        };
        setLabelsCanon(selectedData);
        debug.log(config, 'Selected canon label with color:', selectedData);
      } catch (err) {
        // En cas d'erreur, stocker sans couleur
        const selectedData = { id: label.id, title: label.title };
        setLabelsCanon(selectedData);
        debug.log(config, 'Selected canon label without color (error):', selectedData);
      }
    }
  }, []); // Aucune dépendance car fetchLabelDetails et setLabelsCanon sont stables

  const selectLabelCopied = useCallback(async (label) => {
    if (label && label.id && label.title) {
      try {
        // Récupérer les détails avec couleur
        const labelDetails = await fetchLabelDetails(label.id);
        
        const selectedData = { 
          id: label.id, 
          title: label.title,
          color: labelDetails.color || null
        };
        setLabelsCopied(selectedData);
        debug.log(config, 'Selected copied label with color:', selectedData);
      } catch (err) {
        // En cas d'erreur, stocker sans couleur
        const selectedData = { id: label.id, title: label.title };
        setLabelsCopied(selectedData);
        debug.log(config, 'Selected copied label without color (error):', selectedData);
      }
    }
  }, []); // Aucune dépendance car fetchLabelDetails et setLabelsCopied sont stables

  // Fonction pour réinitialiser toutes les sélections dépendantes
  const resetDependentSelections = useCallback((fromLevel) => {
    switch (fromLevel) {
      case 'worlds':
        manuscripts.clearSelection();
        versions.clearSelection();
        setBelTagsRef.current([]);
        setLabelsDraft(null);
        setLabelsCanon(null);
        setLabelsCopied(null);
        setWaManuscriptLabelsRef.current({});
        break;
      case 'manuscripts':
        versions.clearSelection();
        setBelTagsRef.current({});
        setLabelsDraft(null);
        setLabelsCanon(null);
        setLabelsCopied(null);
        setWaManuscriptLabelsRef.current({});
        break;
      case 'versions':
        // Les versions n'ont pas de dépendances
        break;
    }
    // Les setters de useLocalStorage sont stables, pas besoin de les mettre en dépendances
    // manuscripts.clearSelection et versions.clearSelection changent quand l'utilisateur intervient
  }, []);

  return {
    // Listes principales
    worlds: {
      ...worlds,
      selectItem: (item) => {
        worlds.selectItem(item);
        resetDependentSelections('worlds');
      }
    },
    manuscripts: {
      ...manuscripts,
      selectItem: (item) => {
        manuscripts.selectItem(item);
        resetDependentSelections('manuscripts');
      }
    },
    versions: {
      ...versions,
      selectItem: (item) => {
        versions.selectItem(item);
        resetDependentSelections('versions');
      }
    },
    
    // Bel Tags - combinaison WA + config locale
    belTags: {
      items: belTags,
      loading: belTagsLoading,
      error: belTagsError,
      reload: loadBelTags,
      // Fonction utilitaire pour rechercher un tag par ID
      getById: (id) => belTags[id] || null,
      // Fonction pour obtenir tous les tags sous forme d'array
      getAsArray: () => Object.values(belTags),
      // Fonction pour obtenir les IDs de tous les tags
      getIds: () => Object.keys(belTags),
      // Tous les tags sont déjà filtrés par langue UI lors de la construction
      displayTags: belTags
    },
    
    // Labels
    labels: {
      items: labelsItems,
      loading: labelsLoading,
      error: labelsError,
      reload: loadLabels,
      manuscriptLabels: waManuscriptLabels, // Structure indexée par ID avec titre et couleur
      draft: {
        selectedValue: labelsDraft,
        selectItem: selectLabelDraft,
        clearSelection: () => setLabelsDraft(null)
      },
      canon: {
        selectedValue: labelsCanon,
        selectItem: selectLabelCanon,
        clearSelection: () => setLabelsCanon(null)
      },
      copied: {
        selectedValue: labelsCopied,
        selectItem: selectLabelCopied,
        clearSelection: () => setLabelsCopied(null)
      }
    },

    // Informations globales
    hasValidUser: !!waUser?.id,
    currentVersion: versions.selectedValue
  };
}

export default useWorldAnvilLists;
